import React, { useContext, useState } from 'react';
import { styled, useTheme } from '@mui/material/styles';
import { 
    Box, 
    CssBaseline, 
    AppBar as MuiAppBar, 
    Toolbar, 
    Typography, 
    IconButton, 
    Drawer, 
    List, 
    Avatar,
    Menu,
    MenuItem,
    Divider,
    ListItemIcon,
    useMediaQuery
} from '@mui/material';

// Iconos
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser } from '@fortawesome/free-solid-svg-icons';

// Contextos
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AuthContext from '../../navigation/context/authContext';
import UserContext from '../../navigation/context/userContext';
import { imagesURL } from '../../apiRoutes/apiBase';

// Componentes
import MainListItems from './mainList';
import SecurityListItems from '../dashboardEmpresa/securityList';
import Copyright from '../../components/copyRight';

// Assets
import logoQUD from '../../assets/images/logos/logoATE.svg';
import en_language_img from '../../assets/images/countries/usa.png';
import es_language_img from '../../assets/images/countries/spain.png';

const DRAWER_WIDTH = 260;

// --- ESTILOS ---

const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })(
    ({ theme, open }) => ({
        flexGrow: 1,
        // CORRECCIÓN 1: Quitamos el padding por defecto para que el contenido pueda tocar los bordes
        padding: 0, 
        transition: theme.transitions.create('margin', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
        }),
        marginLeft: `-${DRAWER_WIDTH}px`,
        ...(open && {
            transition: theme.transitions.create('margin', {
                easing: theme.transitions.easing.easeOut,
                duration: theme.transitions.duration.enteringScreen,
            }),
            marginLeft: 0,
        }),
        backgroundColor: '#fff', 
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        overflowX: 'hidden',
    }),
);

const AppBar = styled(MuiAppBar, {
    shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
    backgroundColor: '#FFFFFF',
    color: theme.palette.text.primary,
    boxShadow: 'none',
    borderBottom: 'none', 
    transition: theme.transitions.create(['margin', 'width'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    ...(open && {
        width: `calc(100% - ${DRAWER_WIDTH}px)`,
        marginLeft: DRAWER_WIDTH,
        transition: theme.transitions.create(['margin', 'width'], {
        easing: theme.transitions.easing.easeOut,
        duration: theme.transitions.duration.enteringScreen,
        }),
    }),
}));

export default function ClientLayout(props) {
    const { t, i18n } = useTranslation();
    const theme = useTheme();
    const history = useHistory();
    const isMobile = useMediaQuery(theme.breakpoints.down('md')); 
    
    const { userData } = useContext(UserContext);
    const { signOut } = useContext(AuthContext);

    const [open, setOpen] = useState(!isMobile); 
    const [anchorEl, setAnchorEl] = useState(null);

    const handleDrawerOpen = () => setOpen(true);
    const handleDrawerClose = () => setOpen(false);
    const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
    const handleMenuClose = () => setAnchorEl(null);

    const handleSignOut = () => {
        handleMenuClose();
        history.replace('/');
        signOut();
    };

    const handleLanguage = () => {
        i18n.changeLanguage(i18n.language === 'es' ? 'en' : 'es');
    };

    const redirect = (route) => {
        history.push(route);
        handleMenuClose();
    };

    return (
        <Box sx={{ display: 'flex' }}>
            <CssBaseline />
            
            {/* --- 1. APP BAR (Header Derecho) --- */}
            <AppBar position="fixed" open={open}>
                <Toolbar sx={{ borderBottom: '1px solid #e0e0e0' }}> 
                    <IconButton
                        color="inherit"
                        aria-label="open drawer"
                        onClick={handleDrawerOpen}
                        edge="start"
                        sx={{ mr: 2, ...(open && { display: 'none' }) }}
                    >
                        <MenuIcon />
                    </IconButton>

                    <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1, fontWeight: 700 }}>
                        Dashboard
                    </Typography>

                    <IconButton onClick={handleLanguage} sx={{ mr: 1 }}>
                        <img src={i18n.language === 'es' ? es_language_img : en_language_img} alt="Lang" style={{ width: 24 }} />
                    </IconButton>

                    <IconButton onClick={handleMenuOpen} sx={{ p: 0 }}>
                        <Avatar 
                            src={imagesURL + 'users/' + userData.user.imagen}
                            sx={{ width: 35, height: 35, border: '1px solid #e0e0e0' }} 
                        />
                    </IconButton>

                    <Menu
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl)}
                        onClose={handleMenuClose}
                        PaperProps={{
                            elevation: 0,
                            sx: {
                                overflow: 'visible',
                                filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.1))',
                                mt: 1.5,
                                minWidth: 180,
                                borderRadius: 2,
                                border: '1px solid #e0e0e0'
                            },
                        }}
                        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                    >
                        <MenuItem onClick={() => redirect('/profile')}>
                            <ListItemIcon><FontAwesomeIcon icon={faUser} /></ListItemIcon>
                            {t('helpers.profile')}
                        </MenuItem>
                        <Divider />
                        <MenuItem onClick={handleSignOut} sx={{ color: 'error.main' }}>
                            <ListItemIcon><ExitToAppIcon fontSize="small" color="error" /></ListItemIcon>
                            Cerrar sesión
                        </MenuItem>
                    </Menu>
                </Toolbar>
            </AppBar>

            {/* --- 2. DRAWER (Menu Izquierdo) --- */}
            <Drawer
                sx={{
                    width: DRAWER_WIDTH,
                    flexShrink: 0,
                    '& .MuiDrawer-paper': {
                        width: DRAWER_WIDTH,
                        boxSizing: 'border-box',
                        borderRight: '1px solid #e0e0e0',
                        borderTop: 'none', 
                    },
                }}
                variant="persistent"
                anchor="left"
                open={open}
            >
                <Toolbar sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    borderBottom: '1px solid #e0e0e0', 
                    px: 2 
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                         <img src={logoQUD} alt="QUD" style={{ height: 52, objectFit:'contain' }} />
                    </Box>
                    <IconButton onClick={handleDrawerClose}>
                        {theme.direction === 'rtl' ? <ChevronRightIcon /> : <ChevronLeftIcon />}
                    </IconButton>
                </Toolbar>

                <Box sx={{ overflowY: 'auto', flexGrow: 1, py: 2 }}>
                    <List sx={{ px: 2 }}>
                        <MainListItems />
                    </List>
                    <Divider sx={{ my: 2, mx: 3, opacity: 0.5 }} />
                    <List sx={{ px: 2 }}>
                        <SecurityListItems />
                    </List>
                </Box>
                
                <Box sx={{ p: 2, borderTop: '1px solid #fff', mt: 'auto' }}>
                     <Box sx={{ display: 'flex', alignItems: 'center', p: 1, borderRadius: 2, bgcolor: '#f5f5f5' }}>
                        <Avatar 
                            src={imagesURL + 'users/' + userData.user.imagen} 
                            sx={{ bgcolor: theme.palette.primary.main, width: 35, height: 35 }}
                        >
                            {userData.user.name ? userData.user.name.charAt(0) : 'U'}
                        </Avatar>
                        <Box sx={{ ml: 2, overflow: 'hidden' }}>
                            <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
                                {userData.user.name}
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            </Drawer>

            {/* --- 3. MAIN CONTENT --- */}
            <Main open={open}>
                <Toolbar /> 

                {/* CORRECCIÓN 2: Reemplazamos <Container> por <Box> para permitir ancho 100% 
                    sin paddings restrictivos a los lados ni margen arriba (mt: 2) */}
                <Box sx={{ width: '100%', flexGrow: 1 }}>
                    {props.children}
                </Box>
                
                <Box sx={{ width: '100%', p: 3 }}>
                    <Copyright />
                </Box>
            </Main>
        </Box>
    );
}